/**
 * 解析パネルの状態（U154）。走査窓を決めてから Worker に渡す。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CRISPR_NUCLEASES,
  DEFAULT_PRIMER_SETTINGS,
  STARTER_ENZYME_IDS,
  customEnzyme,
  predictedOrfsToFeatures,
  resolveScanWindow,
  type AnalysisKind,
  type AnalysisResult,
  type CrisprNuclease,
  type Interval,
  type PrimerDesignSettings,
  type RestrictionEnzyme,
  type ScanWindow,
  type ViewRange,
} from '@kensuke/genome-core';
import type { ChunkedSequence } from '@kensuke/genome-core';
import type { GenomicFeatureV1 } from '@kensuke/genome-core';
import { analyzeSequenceWindow } from '../../../utils/geneAnalysisAnalysisClient';
import {
  CRISPR_NUCLEASE_KEY,
  PRIMER_SETTINGS_KEY,
  RESTRICTION_PREFS_KEY,
  type AnalysisTab,
  type SequenceOverlayMark,
} from './geneAnalysisAnalysis';

const EMPTY_RESULT: AnalysisResult = {
  kind: 'search',
  hits: [],
  cuts: [],
  fragments: [],
  silent: [],
  orfs: [],
  primer: { forward: null, reverse: null },
  guides: [],
};

function loadJson<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? ({ ...fallback, ...(JSON.parse(raw) as Partial<T>) } as T) : fallback;
  } catch {
    return fallback;
  }
}

function loadEnzymeIds(): string[] {
  if (typeof localStorage === 'undefined') return [...STARTER_ENZYME_IDS];
  try {
    const raw = localStorage.getItem(RESTRICTION_PREFS_KEY);
    if (!raw) return [...STARTER_ENZYME_IDS];
    const parsed = JSON.parse(raw) as { selectedIds?: string[] };
    return parsed.selectedIds?.length ? parsed.selectedIds : [...STARTER_ENZYME_IDS];
  } catch {
    return [...STARTER_ENZYME_IDS];
  }
}

function loadCustomEnzymes(): RestrictionEnzyme[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(RESTRICTION_PREFS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { customs?: RestrictionEnzyme[] };
    return parsed.customs ?? [];
  } catch {
    return [];
  }
}

export function tabToKind(tab: AnalysisTab): AnalysisKind | null {
  if (tab === 'search' || tab === 'restriction' || tab === 'primer' || tab === 'crispr') return tab;
  if (tab === 'plasmid' || tab === 'info') return 'orf';
  return null;
}

export function useGeneAnalysisAnalysis(params: {
  sequence: ChunkedSequence | null;
  features: GenomicFeatureV1[];
  view: ViewRange;
  selection: Interval | null;
  tab: AnalysisTab;
  sequenceId: string | null;
}) {
  const { sequence, features, view, selection, tab, sequenceId } = params;
  const [query, setQuery] = useState('');
  const [bothStrands, setBothStrands] = useState(true);
  const [enzymeIds, setEnzymeIds] = useState<string[]>(loadEnzymeIds);
  const [customEnzymes, setCustomEnzymes] = useState<RestrictionEnzyme[]>(loadCustomEnzymes);
  const [primerSettings, setPrimerSettings] = useState<PrimerDesignSettings>(() =>
    loadJson(PRIMER_SETTINGS_KEY, DEFAULT_PRIMER_SETTINGS)
  );
  const [nucleaseId, setNucleaseId] = useState(() => {
    if (typeof localStorage === 'undefined') return CRISPR_NUCLEASES[0].id;
    return localStorage.getItem(CRISPR_NUCLEASE_KEY) ?? CRISPR_NUCLEASES[0].id;
  });
  const [customPam, setCustomPam] = useState('');
  const [result, setResult] = useState<AnalysisResult>(EMPTY_RESULT);
  const [windowInfo, setWindowInfo] = useState<ScanWindow | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hitLimit, setHitLimit] = useState(80);
  const runIdRef = useRef(0);

  useEffect(() => {
    setResult(EMPTY_RESULT);
    setQuery('');
    setActiveIndex(0);
    setError(null);
  }, [sequenceId]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(
      RESTRICTION_PREFS_KEY,
      JSON.stringify({ selectedIds: enzymeIds, customs: customEnzymes })
    );
  }, [enzymeIds, customEnzymes]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(PRIMER_SETTINGS_KEY, JSON.stringify(primerSettings));
  }, [primerSettings]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(CRISPR_NUCLEASE_KEY, nucleaseId);
  }, [nucleaseId]);

  const scanWindow = useMemo(() => {
    if (!sequence) return null;
    return resolveScanWindow({
      genomeLength: sequence.length,
      topology: sequence.topology,
      view,
      selection,
    });
  }, [sequence, view, selection]);

  const run = useCallback(
    async (kind: AnalysisKind, force = false) => {
      if (!sequence || !scanWindow) return;
      if (kind === 'search' && query.replace(/\s+/g, '').length < 3) {
        setResult(EMPTY_RESULT);
        return;
      }
      const live = sequence.length <= 100_000;
      if (kind === 'search' && !live && !force) return;

      const id = (runIdRef.current += 1);
      setRunning(true);
      setError(null);
      try {
        const codes = await sequence.read(scanWindow.interval);
        if (id !== runIdRef.current) return;
        const customNuclease: CrisprNuclease | undefined =
          customPam && /^[ACGTURYSWKMBDHVN]+$/i.test(customPam)
            ? { id: 'custom', name: 'Custom', pam: customPam.toUpperCase(), spacer: 20, pamSide: "3'" }
            : undefined;
        const next = await analyzeSequenceWindow(
          {
            kind,
            genomeLength: sequence.length,
            topology: sequence.topology,
            interval: scanWindow.interval,
            search: { pattern: query, bothStrands, limit: 2000 },
            restriction: { enzymeIds, extraEnzymes: customEnzymes },
            primer: primerSettings,
            crispr: { nucleaseId, custom: customNuclease, limit: 80 },
          },
          codes
        );
        if (id !== runIdRef.current) return;
        setResult((previous) => ({
          ...next,
          hits: next.kind === 'search' ? next.hits : previous.hits,
          cuts: next.kind === 'restriction' ? next.cuts : previous.cuts,
          fragments: next.kind === 'restriction' ? next.fragments : previous.fragments,
          silent: next.kind === 'restriction' ? next.silent : previous.silent,
          guides: next.kind === 'crispr' ? next.guides : previous.guides,
          primer: next.kind === 'primer' ? next.primer : previous.primer,
          orfs: next.kind === 'orf' ? next.orfs : previous.orfs,
        }));
        setWindowInfo(scanWindow);
        setActiveIndex(0);
        setHitLimit(80);
      } catch (caught) {
        if (id !== runIdRef.current) return;
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        if (id === runIdRef.current) setRunning(false);
      }
    },
    [
      bothStrands,
      customEnzymes,
      customPam,
      enzymeIds,
      nucleaseId,
      primerSettings,
      query,
      scanWindow,
      sequence,
    ]
  );

  const kind = tabToKind(tab);

  useEffect(() => {
    if (!kind) return;
    const delay = kind === 'search' ? 180 : 80;
    const timer = window.setTimeout(() => {
      void run(kind, false);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [kind, run]);

  const addCustomEnzyme = useCallback((name: string, pattern: string, cutAfter: number, overhang: number) => {
    const enzyme = customEnzyme(name, pattern, cutAfter, overhang);
    if (!enzyme) return false;
    setCustomEnzymes((previous) => [...previous.filter((item) => item.id !== enzyme.id), enzyme]);
    setEnzymeIds((previous) => (previous.includes(enzyme.id) ? previous : [...previous, enzyme.id]));
    return true;
  }, []);

  const predictedFeatures = useMemo(
    () => predictedOrfsToFeatures(result.orfs, features),
    [result.orfs, features]
  );

  const overlays = useMemo<SequenceOverlayMark[]>(() => {
    if (tab === 'search') {
      return result.hits.slice(0, hitLimit).map((hit, index) => ({
        start: hit.start,
        end: hit.end,
        color: index === activeIndex ? '#2563EB' : '#93C5FD',
        kind: 'search' as const,
        active: index === activeIndex,
      }));
    }
    if (tab === 'restriction') {
      return result.cuts.map((cut, index) => ({
        start: cut.cut,
        end: cut.cut + 1,
        color: cut.unique ? '#16A34A' : index === activeIndex ? '#DC2626' : '#F97316',
        kind: 'cut' as const,
        active: index === activeIndex,
      }));
    }
    if (tab === 'crispr') {
      return result.guides.map((guide, index) => ({
        start: guide.start,
        end: guide.end,
        color: index === activeIndex ? '#7C3AED' : '#C4B5FD',
        kind: 'guide' as const,
        active: index === activeIndex,
      }));
    }
    if (tab === 'primer') {
      const marks: SequenceOverlayMark[] = [];
      if (result.primer.forward) {
        marks.push({
          start: result.primer.forward.start,
          end: result.primer.forward.end,
          color: '#0D9488',
          kind: 'primer',
        });
      }
      if (result.primer.reverse) {
        marks.push({
          start: result.primer.reverse.start,
          end: result.primer.reverse.end,
          color: '#DB2777',
          kind: 'primer',
        });
      }
      return marks;
    }
    return [];
  }, [activeIndex, hitLimit, result, tab]);

  return {
    query,
    setQuery,
    bothStrands,
    setBothStrands,
    enzymeIds,
    setEnzymeIds,
    customEnzymes,
    addCustomEnzyme,
    primerSettings,
    setPrimerSettings,
    nucleaseId,
    setNucleaseId,
    customPam,
    setCustomPam,
    result,
    windowInfo,
    running,
    error,
    activeIndex,
    setActiveIndex,
    hitLimit,
    setHitLimit,
    run: (force = true) => (kind ? run(kind, force) : Promise.resolve()),
    predictedFeatures,
    overlays,
    scanWindow,
  };
}

export type GeneAnalysisAnalysisState = ReturnType<typeof useGeneAnalysisAnalysis>;
